import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { Phone, Loader2, Lock, CreditCard, CheckCircle2, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PlanOption = ({ plan, selected, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(plan.id)}
    className={`text-left p-4 rounded-xl border-2 transition-colors ${selected ? 'border-blue-600 bg-blue-50' : 'border-slate-200 bg-white hover:border-slate-300'}`}
    data-testid={`join-plan-${plan.id}`}
  >
    <div className="flex items-center justify-between">
      <p className="font-semibold text-slate-900">{plan.name}</p>
      {selected && <CheckCircle2 className="w-5 h-5 text-blue-600" />}
    </div>
    <p className="text-2xl font-bold text-slate-900 mt-1">${plan.monthly_price}<span className="text-sm font-normal text-slate-500">/mo</span></p>
    <p className="text-xs text-slate-500 mt-1">{plan.minutes_included} minutes included{plan.setup_fee ? ` · $${plan.setup_fee} one-time setup` : ''}</p>
  </button>
);

export default function JoinInvite() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [planId, setPlanId] = useState('starter');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetch(`${API_URL}/api/invites/${token}`)
      .then(async (r) => {
        const d = await r.json();
        if (!r.ok) throw new Error(d.detail);
        setInvite(d);
      })
      .catch((e) => setError(e.message || 'This link is invalid'));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) return toast.error('Password must be at least 6 characters');
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/invites/${token}/accept`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password, plan_id: planId, owner_phone_number: phone || undefined, origin_url: window.location.origin }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || 'Signup failed');
      login(d.user, d.token);
      if (d.checkout_url) {
        toast.success('Account created! Taking you to secure card payment...');
        window.location.href = d.checkout_url;
      } else {
        toast.warning('Account created. Please finish payment from your dashboard.');
        navigate(`/business/${d.business.id}`);
      }
    } catch (err) {
      toast.error(err.message);
      setSubmitting(false);
    }
  };

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="p-8 max-w-md text-center bg-white border-slate-200" data-testid="join-error">
          <h1 className="text-xl font-semibold text-slate-900 mb-2">Link unavailable</h1>
          <p className="text-slate-500 mb-6">{error}</p>
          <Link to="/login" className="text-blue-600 font-medium">Go to login</Link>
        </Card>
      </div>
    );
  }

  if (!invite) {
    return <div className="min-h-screen bg-slate-50 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;
  }

  return (
    <div className="min-h-screen bg-slate-50 py-10 px-4" data-testid="join-page">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-xl bg-blue-600 flex items-center justify-center"><Phone className="w-5 h-5 text-white" /></div>
          <span className="text-xl font-bold text-slate-900">Front Desk Pro</span>
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 leading-tight mb-3" data-testid="join-title">
          Your AI receptionist for {invite.business_name} is ready.
        </h1>
        <p className="text-base text-slate-600 mb-8">{invite.rep_name} saved everything from your demo: greeting, business knowledge, voice and pacing. Create your account and it goes live.</p>

        {invite.greeting && (
          <div className="mb-8 p-5 rounded-xl bg-white border border-slate-200 border-l-4 border-l-blue-600">
            <p className="text-xs uppercase tracking-wide text-slate-400 mb-1">What your callers will hear</p>
            <p className="text-slate-800 italic" data-testid="join-greeting">"{invite.greeting}"</p>
          </div>
        )}

        <Card className="p-6 bg-white border-slate-200">
          <form onSubmit={submit} className="space-y-5">
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Email</label>
              <Input value={invite.email} readOnly className="bg-slate-50 border-slate-300 text-slate-700" data-testid="join-email" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Create a password</label>
              <div className="relative">
                <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="At least 6 characters" className="pl-9 bg-white border-slate-300 text-slate-900" data-testid="join-password" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">Your cell phone (for call transfers)</label>
              <Input type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" className="bg-white border-slate-300 text-slate-900" data-testid="join-phone" />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700 mb-2 block">Choose your plan</label>
              <div className="grid sm:grid-cols-2 gap-3">
                {invite.plans.map((p) => <PlanOption key={p.id} plan={p} selected={planId === p.id} onSelect={setPlanId} />)}
              </div>
            </div>
            <Button type="submit" disabled={submitting} className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white text-base" data-testid="join-submit-btn">
              {submitting ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <CreditCard className="w-5 h-5 mr-2" />}
              Create Account & Pay by Card
            </Button>
            <p className="text-xs text-slate-500 flex items-center gap-1 justify-center">
              <ShieldCheck className="w-4 h-4" /> Visa, Mastercard, Amex & Discover accepted on our secure checkout.
            </p>
          </form>
        </Card>
      </div>
    </div>
  );
}
